import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client';
import { mutations, queriesV2 } from '../../../api';
import { useNotifications } from '../../../notifications';
import { TitleFormattedMessage } from '../../../components/titleFormattedMessage';
import { getTemplateFolders } from '../../../api/queries_v2';
import { FolderTreeSelector } from '../../../components/folder-tree-selector';
import PropdocsModal from '../../../components/modal/PropdocsModal';

type MoveTemplateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  templateName: string;
  teamId: string;
  onSuccess: () => void;
};

const MoveTemplateModal = ({
  isOpen,
  onClose,
  templateId,
  templateName,
  teamId,
  onSuccess,
}: MoveTemplateModalProps) => {
  const { formatMessage } = useIntl();
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();

  // Fetch all template folders to show as destination options
  const { data: foldersData, loading: foldersLoading } = useQuery(getTemplateFolders as any, {
    variables: { teamId, folderType: 'template' },
    skip: !isOpen,
  });

  // Use the existing moveTemplateToFolder mutation
  const [moveTemplateToFolder] = useMutation(mutations.moveTemplateToFolder as any, {
    onCompleted: () => {
      notifySuccess({ message: 'Template moved successfully' });
      onSuccess();
      onClose();
      setIsLoading(false);
    },
    onError: (error) => {
      notifyError({ message: error.message || 'Failed to move template' });
      setIsLoading(false);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsLoading(true);
    try {
      await moveTemplateToFolder({
        variables: {
          templateId: templateId,
          folderId: selectedFolderId, // null for root level
        },
        refetchQueries: [
          {
            query: queriesV2.getTemplateFolders,
            variables: { teamId, folderType: 'template' },
          },
          {
            query: queriesV2.getDocumentTemplates,
            variables: { teamId, templateType: 'agreement' },
          },
        ],
      });
    } catch (error) {
      console.error('Error moving template:', error);
      notifyError({ message: 'Failed to move template' });
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedFolderId(null);
    onClose();
  };

  const availableFolders = foldersData?.getTemplateFolders || [];

  return (
    <PropdocsModal
      show={isOpen}
      setShow={handleClose}
      size="lg"
      disableClose={isLoading}
      title={<FormattedMessage id="title.move-template" defaultMessage="Move Template" />}
    >
      <Form onSubmit={handleSubmit}>
        <>
          <div style={{ marginBottom: '1.5rem' }}>
            <div className="mb-3">
              <strong>
                <FormattedMessage
                  id="message.moving-template"
                  defaultMessage="Moving: {templateName}"
                  values={{ templateName }}
                />
              </strong>
            </div>

            <Form.Group>
              <Form.Label>
                <FormattedMessage id="label.destination-folder" defaultMessage="Destination Folder" />
              </Form.Label>
              {foldersLoading ? (
                <div className="text-muted">
                  <FormattedMessage id="message.loading-folders" defaultMessage="Loading folders..." />
                </div>
              ) : (
                <FolderTreeSelector
                  folders={availableFolders}
                  selectedFolderId={selectedFolderId}
                  onSelectionChange={setSelectedFolderId}
                  disabled={isLoading}
                  context="templates"
                />
              )}
              <div className="form-text mt-2">
                <FormattedMessage
                  id="templates.move.help"
                  defaultMessage="Choose the destination folder for this template. Select 'All Templates (Root)' to move it to the top level."
                />
              </div>
            </Form.Group>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem' }}>
            <Button variant="link" className="text-secondary" onClick={handleClose} disabled={isLoading}>
              <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
            </Button>
            <Button variant="primary" type="submit" disabled={isLoading || foldersLoading}>
              {isLoading ? (
                <FormattedMessage id="cta.moving" defaultMessage="Moving..." />
              ) : (
                <TitleFormattedMessage id="cta.move" defaultMessage="Move" />
              )}
            </Button>
          </div>
        </>
      </Form>
    </PropdocsModal>
  );
};

export default MoveTemplateModal;
